/**
 * useFinance hooks — Issue #134
 *
 * Typed hooks for Finance module API calls using @jinzhuan/api-client.
 * All monetary amounts are transmitted as strings (Decimal precision).
 *
 * Red lines:
 *   - MUST use @jinzhuan/api-client; no hand-written fetch/axios.
 *   - No fake frontend state; all state comes from API responses.
 */

import { useState, useCallback } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// 财务模块端点都受 RBAC + 租户隔离 + 项目级解锁约束保护；typed client 必须
// 通过共享的 tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// ─────────────────────────────────────────────────────────────────────────────
// Type aliases
// ─────────────────────────────────────────────────────────────────────────────

type OrderProjectResponseDto = ApiComponents['schemas']['OrderProjectResponseDto'];
type CreateOrderProjectDto = ApiComponents['schemas']['CreateOrderProjectDto'];

type PaymentRecordResponseDto = ApiComponents['schemas']['PaymentRecordResponseDto'];
type CreatePaymentRecordDto = ApiComponents['schemas']['CreatePaymentRecordDto'];
type ConfirmPaymentRecordDto = ApiComponents['schemas']['ConfirmPaymentRecordDto'];
type VoidPaymentRecordDto = ApiComponents['schemas']['VoidPaymentRecordDto'];
type ReversePaymentRecordDto = ApiComponents['schemas']['ReversePaymentRecordDto'];

type ProjectUnlockResponseDto = ApiComponents['schemas']['ProjectUnlockResponseDto'];
type SpecialOverrideUnlockDto = ApiComponents['schemas']['SpecialOverrideUnlockDto'];

type RevenueLedgerResponseDto = ApiComponents['schemas']['RevenueLedgerResponseDto'];
type CreateRevenueLedgerDto = ApiComponents['schemas']['CreateRevenueLedgerDto'];
type UnlockRevenueLedgerDto = ApiComponents['schemas']['UnlockRevenueLedgerDto'];
type PayOffRevenueLedgerDto = ApiComponents['schemas']['PayOffRevenueLedgerDto'];

type CommissionLedgerResponseDto = ApiComponents['schemas']['CommissionLedgerResponseDto'];
type CreateCommissionLedgerDto = ApiComponents['schemas']['CreateCommissionLedgerDto'];
type PayOffCommissionLedgerDto = ApiComponents['schemas']['PayOffCommissionLedgerDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, httpStatus?: number): ApiError {
  // openapi-fetch middleware throws ApiClientError on non-2xx responses, so
  // the catch path supplies the status via the thrown error instead of via
  // the explicit `httpStatus` argument. Fall back to it when the caller did
  // not pass an explicit status (Issue #226).
  const status = httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (status === 409) {
    return { message: '数据已被其他用户修改，请刷新页面后重试', code: 'CONFLICT' };
  }
  if (status === 403) {
    return { message: '项目未解锁，请先完成付款或申请特批解锁', code: 'FORBIDDEN' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

// ─────────────────────────────────────────────────────────────────────────────
// PaymentRecord hooks
// ─────────────────────────────────────────────────────────────────────────────

export function usePaymentRecords() {
  const [records, setRecords] = useState<PaymentRecordResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchRecords = useCallback(
    async (params?: { orderProjectId?: string; orderId?: string; status?: string; limit?: number; offset?: number }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET('/api/v1/finance/payment-records', {
          params: { query: params },
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setRecords([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { records, loading, error, fetchRecords };
}

export function usePaymentRecordActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const createRecord = useCallback(async (dto: CreatePaymentRecordDto): Promise<PaymentRecordResponseDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/finance/payment-records', {
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const confirmRecord = useCallback(async (id: string, dto: ConfirmPaymentRecordDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/finance/payment-records/{id}/confirm', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const voidRecord = useCallback(async (id: string, dto: VoidPaymentRecordDto): Promise<PaymentRecordResponseDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/finance/payment-records/{id}/void', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const reverseRecord = useCallback(async (id: string, dto: ReversePaymentRecordDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/finance/payment-records/{id}/reverse', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, createRecord, confirmRecord, voidRecord, reverseRecord };
}

// ─────────────────────────────────────────────────────────────────────────────
// ProjectUnlock hooks
// ─────────────────────────────────────────────────────────────────────────────

export function useProjectUnlocks() {
  const [unlocks, setUnlocks] = useState<ProjectUnlockResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchUnlocks = useCallback(
    async (params?: { orderProjectId?: string; status?: string; limit?: number; offset?: number }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET('/api/v1/finance/project-unlocks', {
          params: { query: params },
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setUnlocks([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const specialOverride = useCallback(async (dto: SpecialOverrideUnlockDto): Promise<ProjectUnlockResponseDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/finance/project-unlocks/special-override', {
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { unlocks, loading, error, fetchUnlocks, specialOverride };
}

// ─────────────────────────────────────────────────────────────────────────────
// RevenueLedger hooks
// ─────────────────────────────────────────────────────────────────────────────

export function useRevenueLedgers() {
  const [ledgers, setLedgers] = useState<RevenueLedgerResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchLedgers = useCallback(
    async (params?: { orderProjectId?: string; status?: string; limit?: number; offset?: number }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET('/api/v1/finance/revenue-ledgers', {
          params: { query: params },
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setLedgers([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createLedger = useCallback(async (dto: CreateRevenueLedgerDto): Promise<RevenueLedgerResponseDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/finance/revenue-ledgers', {
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const unlockLedger = useCallback(async (id: string, dto: UnlockRevenueLedgerDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/finance/revenue-ledgers/{id}/unlock', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const payOffLedger = useCallback(async (id: string, dto: PayOffRevenueLedgerDto): Promise<RevenueLedgerResponseDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/finance/revenue-ledgers/{id}/pay-off', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { ledgers, loading, error, fetchLedgers, createLedger, unlockLedger, payOffLedger };
}

// ─────────────────────────────────────────────────────────────────────────────
// CommissionLedger hooks
// ─────────────────────────────────────────────────────────────────────────────

export function useCommissionLedgers() {
  const [ledgers, setLedgers] = useState<CommissionLedgerResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchLedgers = useCallback(
    async (params?: { orderProjectId?: string; staffId?: string; status?: string; limit?: number; offset?: number }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET('/api/v1/finance/commission-ledgers', {
          params: { query: params },
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setLedgers([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createLedger = useCallback(async (dto: CreateCommissionLedgerDto): Promise<CommissionLedgerResponseDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/finance/commission-ledgers', {
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const payOffLedger = useCallback(async (id: string, dto: PayOffCommissionLedgerDto): Promise<CommissionLedgerResponseDto | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST('/api/v1/finance/commission-ledgers/{id}/pay-off', {
        params: { path: { id } },
        body: dto,
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { ledgers, loading, error, fetchLedgers, createLedger, payOffLedger };
}

// Re-export types for page components
export type {
  OrderProjectResponseDto,
  CreateOrderProjectDto,
  PaymentRecordResponseDto,
  CreatePaymentRecordDto,
  ConfirmPaymentRecordDto,
  VoidPaymentRecordDto,
  ReversePaymentRecordDto,
  ProjectUnlockResponseDto,
  SpecialOverrideUnlockDto,
  RevenueLedgerResponseDto,
  CreateRevenueLedgerDto,
  UnlockRevenueLedgerDto,
  PayOffRevenueLedgerDto,
  CommissionLedgerResponseDto,
  CreateCommissionLedgerDto,
  PayOffCommissionLedgerDto,
};
